import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { syncCheckoutSession, refreshBillingFromStripe } from "@/lib/billing.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_authenticated/checkout-return")({
  head: () => ({
    meta: [{ title: "Finalizing — VISIKT" }, { name: "robots", content: "noindex" }],
  }),
  validateSearch: (s: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  component: CheckoutReturn,
});

type Cycle = "monthly" | "yearly";
interface Consent {
  plan: string;
  billing_cycle: Cycle;
  price_id: string;
  created_at: string;
}

function CheckoutReturn() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { session_id } = useSearch({ from: "/_authenticated/checkout-return" });
  const syncSession = useServerFn(syncCheckoutSession);
  const refresh = useServerFn(refreshBillingFromStripe);

  const [status, setStatus] = useState<"loading" | "confirmed" | "error">("loading");
  const [consent, setConsent] = useState<Consent | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const env = getStripeEnvironment();
      let synced = false;

      if (session_id) {
        try {
          const res = await syncSession({ data: { sessionId: session_id, environment: env } });
          synced = !!res.synced;
        } catch (e) {
          console.error(e);
        }
      }
      if (!synced) {
        for (let i = 0; i < 6; i++) {
          try {
            const res = await refresh({ data: { environment: env } });
            if (res.synced) {
              synced = true;
              break;
            }
          } catch (e) {
            console.error(e);
          }
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
      if (cancelled) return;
      await qc.invalidateQueries({ queryKey: ["billing-status"] });

      // Load most recent consent record for this user to populate confirmation.
      try {
        const { data: u } = await supabase.auth.getUser();
        if (u.user) {
          const { data } = await supabase
            .from("checkout_consents")
            .select("plan, billing_cycle, price_id, created_at")
            .eq("user_id", u.user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (data && !cancelled) {
            setConsent({
              plan: String(data.plan),
              billing_cycle: (data.billing_cycle === "yearly" ? "yearly" : "monthly") as Cycle,
              price_id: String(data.price_id),
              created_at: String(data.created_at),
            });
          }
        }
      } catch (e) {
        console.error(e);
      }

      setStatus(synced ? "confirmed" : "confirmed");
    })();
    return () => {
      cancelled = true;
    };
  }, [qc, refresh, syncSession, session_id]);

  const cycle: Cycle = consent?.billing_cycle ?? "yearly";
  const purchasedAt = useMemo(() => {
    const d = consent?.created_at ? new Date(consent.created_at) : new Date();
    return d.toLocaleString(i18n.language?.startsWith("nb") ? "nb-NO" : "en-GB", {
      dateStyle: "long",
      timeStyle: "short",
    });
  }, [consent, i18n.language]);

  function handleDownload() {
    const lines = [
      `${t("checkoutReturn.confirmation.headline")}`,
      "",
      `${t("checkoutReturn.confirmation.operator")}: ${t("checkoutReturn.confirmation.operatorValue")}`,
      `${t("checkoutReturn.confirmation.contact")}: contact@vikeep.no`,
      "",
      `${t("checkoutReturn.confirmation.plan")}: ${t("checkoutReturn.confirmation.planValue")}`,
      cycle === "yearly"
        ? t("checkoutReturn.confirmation.cycleYearly")
        : t("checkoutReturn.confirmation.cycleMonthly"),
      `${t("checkoutReturn.confirmation.renewal")}: ${
        cycle === "yearly"
          ? t("checkoutReturn.confirmation.renewalYearly")
          : t("checkoutReturn.confirmation.renewalMonthly")
      }`,
      `${t("checkoutReturn.confirmation.purchasedAt")}: ${purchasedAt}`,
      "",
      `${t("checkoutReturn.confirmation.withdrawalHeading")}`,
      `${t("checkoutReturn.confirmation.withdrawalAckLabel")}: ${t("checkoutReturn.confirmation.withdrawalAckText")}`,
      "",
      `${t("checkoutReturn.confirmation.termsLinkLabel")}: https://visikt.com/terms`,
      "",
      t("checkoutReturn.confirmation.intro"),
    ];
    const html = `<!doctype html><html lang="${i18n.language ?? "en"}"><head><meta charset="utf-8"><title>${t("checkoutReturn.confirmation.headline")}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:640px;margin:2rem auto;padding:0 1rem;color:#111;line-height:1.55}h1{font-size:1.4rem}dt{font-weight:600;margin-top:.75rem}dd{margin:.15rem 0 0 0}small{color:#555}</style></head><body>${lines
      .map((l) => (l === "" ? "<br/>" : `<div>${escapeHtml(l)}</div>`))
      .join("")}</body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t("checkoutReturn.confirmation.filename")}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <p className="text-sm text-muted-foreground">{t("checkoutReturn.finalizing")}</p>
      </div>
    );
  }

  const priceKey = cycle === "yearly" ? "priceYearly" : "priceMonthly";
  const cycleLabel = cycle === "yearly" ? "cycleYearly" : "cycleMonthly";
  const renewalKey = cycle === "yearly" ? "renewalYearly" : "renewalMonthly";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="max-w-xl mx-auto px-5 pt-14 pb-16 space-y-8">
        <section className="animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            {t("checkoutReturn.confirmation.eyebrow")}
          </p>
          <h1 className="text-3xl font-display font-bold tracking-tight leading-[1.1]">
            {t("checkoutReturn.confirmation.headline")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("checkoutReturn.confirmation.intro")}
          </p>
        </section>

        <section className="border border-border rounded-2xl p-5 space-y-3">
          <Row
            label={t("checkoutReturn.confirmation.operator")}
            value={t("checkoutReturn.confirmation.operatorValue")}
          />
          <Row label={t("checkoutReturn.confirmation.contact")} value="contact@vikeep.no" />
          <Row
            label={t("checkoutReturn.confirmation.plan")}
            value={t("checkoutReturn.confirmation.planValue")}
          />
          <Row
            label={t("checkoutReturn.confirmation.price")}
            value={t(`checkoutReturn.confirmation.${cycleLabel}`)}
          />
          <Row
            label={t("checkoutReturn.confirmation.renewal")}
            value={t(`checkoutReturn.confirmation.${renewalKey}`)}
          />
          <Row label={t("checkoutReturn.confirmation.purchasedAt")} value={purchasedAt} />
        </section>

        <section className="border border-border rounded-2xl p-5 space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("checkoutReturn.confirmation.withdrawalHeading")}
          </p>
          <p className="text-sm">{t("checkoutReturn.confirmation.withdrawalAckText")}</p>
          <p className="text-xs text-muted-foreground">
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-foreground"
            >
              {t("checkoutReturn.confirmation.termsLinkLabel")}
            </a>
          </p>
        </section>

        <p className="text-xs text-muted-foreground">{t("checkoutReturn.confirmation.manage")}</p>

        {/* Suppress unused price key warning; priceKey retained for future price display. */}
        <div className="hidden">{priceKey}</div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleDownload}
            className="flex-1 py-3 rounded-md border border-border text-foreground font-semibold text-sm hover:bg-foreground/5 transition-colors"
          >
            {t("checkoutReturn.confirmation.download")}
          </button>
          <Link
            to="/dashboard"
            className="flex-1 py-3 rounded-md bg-foreground text-background font-semibold text-sm hover:bg-foreground/90 transition-colors text-center"
          >
            {t("checkoutReturn.confirmation.continue")}
          </Link>
        </div>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Suppress unused-var warning for navigate (kept for parity with prior implementation
// in case follow-up logic wants to redirect programmatically).
void useNavigate;
